import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, withLatestFrom } from 'rxjs/operators';
import { Column } from 'src/app/services/dtos/column';
import { Mapping } from 'src/app/services/dtos/mapping';
import { Rule } from 'src/app/services/dtos/rule';
import { RuleForm } from 'src/app/services/dtos/ruleForm';
import {
  AddRule,
  EditRule,
  FetchAllMappings,
} from 'src/app/states/mapping-state/mapping.actions';
import { MappingState } from 'src/app/states/mapping-state/mapping.state';

@Component({
  selector: 'app-rule-dialog',
  templateUrl: './rule-dialog.component.html',
  styleUrls: ['./rule-dialog.component.scss'],
})
export class RuleDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  public selectedRuleId$: Observable<string> = inject(Store).select(MappingState.getSelectedRuleId);
  public mappings$: Observable<Mapping[]> = inject(Store).select(MappingState.getAll);
  public activePluginId$: Observable<string> = inject(Store).select(MappingState.getActivePluginId);
  public customerMappingIds$: Observable<string[]> = inject(Store).select(MappingState.getCustomerMappingIds);

  // reference to the MatMenuTrigger in the DOM
  @ViewChild(MatMenuTrigger, { static: true })
  public matMenuTrigger!: MatMenuTrigger;
  // we create an object that contains coordinates
  public menuTopLeftPosition = { x: '0', y: '0' };
  public contextMenuList: Column[] = [];
  public searchContextMenuControl = new FormControl();
  public contextMenuSearchText = '';

  public selectedRuleId!: string;
  public mappings!: Mapping[];
  public activePluginId!: string;
  public customerMappingIds!: string[];
  public selectedCustomerId!: string;

  public form: UntypedFormGroup = this.fb.group({
    targetPath: ['', Validators.required],
    mappingId: ['', Validators.required],
    rule: ['', Validators.required],
    additionalProperties: [''],
    additionalCondition: [''],
    pluginId: ['', Validators.required],
  });

  public ruleNonColumnValues: string[] = [];

  private openPlaceholderListKey = '@';
  private subs = new Array<Subscription>();

  public constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<RuleDialogComponent>,
    public dialog: MatDialog,
    private store: Store
  ) {}

  public ngAfterViewInit(): void {
    this.form.controls.rule.setValue(
      this.replaceArgsWithPlaceholders(this.form.controls.rule.value)
    );
  }

  public async ngOnInit(): Promise<void> {
    // subscribe to value changes of input of the context menu
    this.subs.push(
      this.searchContextMenuControl.valueChanges
        .pipe(debounceTime(750))
        .subscribe((inputText: string) => {
          this.contextMenuSearchText = inputText;

          const filterId = document.getElementById('filter-menu-id');
          filterId?.focus();
        })
    );

    this.subs.push(
      this.customerMappingIds$.subscribe((customerMappings) => {
        if (customerMappings) {
          this.customerMappingIds = customerMappings;
        }
      })
    );

    this.store
      .dispatch(new FetchAllMappings(this.selectedCustomerId))
      .pipe(withLatestFrom(this.mappings$))
      .subscribe(([, mappings]) => {
        this.filterMappingByCustomer(mappings);
      });

    this.subs.push(
      this.activePluginId$.subscribe((activePluginId) => {
        if (activePluginId) {
          this.activePluginId = activePluginId;
          this.form.controls.pluginId.setValue(this.activePluginId);
        }
      })
    );

    this.subs.push(
      this.selectedRuleId$.subscribe((selectedRuleId) => {
        if (selectedRuleId !== '') {
          this.selectedRuleId = selectedRuleId;

          this.subs.push(
            this.mappings$.subscribe((mappings) => {
              if (mappings) {
                this.filterMappingByCustomer(mappings);
                const selectedRule = mappings
                  .map((mapping) => mapping.rules)
                  .reduce((a, b) => a.concat(b), [])
                  .find(({ id }) => id === this.selectedRuleId) as Rule;
                if (selectedRule) {
                  this.form = this.fb.group({
                    targetPath: [selectedRule.targetPath, Validators.required],
                    mappingId: [selectedRule.mappingId, Validators.required],
                    rule: [selectedRule.rule, Validators.required],
                    additionalProperties: [selectedRule.additionalProperties],
                    additionalCondition: [selectedRule.additionalCondition],
                    pluginId: [this.activePluginId],
                  });
                  this.form.controls.rule.setValue(
                    this.replaceArgsWithPlaceholders(
                      this.form.controls.rule.value
                    )
                  );
                }
              }
            })
          );
        }
      })
    );
  }

  public isInFilter(value: string): boolean {
    const query = this.contextMenuSearchText.toLowerCase();
    const showItem = this.contextMenuList
      ?.filter((x: Column) =>
        x.name.toLowerCase().includes(query.toLowerCase())
      )
      .some((x: Column) => x.name === value);

    return showItem;
  }

  public getMappingColumns() {
    const selectedMapping = this.mappings.filter(
      (x) => x.id === this.form.controls.mappingId.value
    );
    return selectedMapping.map((x) => x.columns)[0];
  }

  public openContextMenu(event: KeyboardEvent) {
    // handle context menu
    if (event.key === this.openPlaceholderListKey) {
      event.preventDefault();
      this.contextMenuList = [];
      this.contextMenuSearchText = '';
      this.searchContextMenuControl.setValue('');
      // set the position in the context menu
      this.menuTopLeftPosition.x = '709' + 'px';
      this.menuTopLeftPosition.y = '462' + 'px';

      // we pass to the menu the information about our object
      const contextColumns = this.getMappingColumns();
      contextColumns.forEach((column) => {
        this.contextMenuList.push(column);
      });
      this.matMenuTrigger.menuData = contextColumns;

      // we open the menu
      this.matMenuTrigger.openMenu();
    }
  }

  public addColumnPlaceholder(column: Column) {
    this.form.controls.rule.patchValue(
      this.form.controls.rule.value + `{${column.name}}`
    );
    const ruleTextarea = document.getElementById('rule-textarea');
    ruleTextarea?.focus();
    this.form.markAsDirty();
    this.validateRulePlaceholdersOnMappingChange();
  }

  public ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  public isFormControlInvalid(controlName: string): boolean {
    const formControl = this.form.get(controlName);
    if (formControl === null) {
      return false;
    }

    return formControl.invalid && (formControl.dirty || formControl.touched);
  }

  public getErrorMessage(controlName: string): string {
    const formControl = this.form.get(controlName);
    if (formControl === null) {
      return '';
    }
    if (formControl.hasError('required')) {
      return 'You must enter a value';
    }

    return '';
  }

  public save(): void {
    if (!this.form.untouched && !this.form.pristine && this.canSave()) {
      const ruleForm = this.form.value as RuleForm;
      ruleForm.rule = this.replacePlaceholdersWithArgs(ruleForm.rule);

      if (!this.selectedRuleId) {
        this.store.dispatch(new AddRule(ruleForm, this.selectedCustomerId));
      } else {
        ruleForm.id = this.selectedRuleId;
        this.store.dispatch(new EditRule(ruleForm, this.selectedCustomerId));
      }
      this.dialogRef.close();
    }
  }

  public canSave(): boolean {
    if (this.form) {
      return this.form.valid && this.form.dirty;
    }
    return false;
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public validateRulePlaceholdersOnMappingChange() {
    const ruleForm = this.form.value as RuleForm;
    this.validateRulePlaceholders(ruleForm.rule);
  }

  public validateRulePlaceholders(ruleWithPlaceholders: string) {
    let rule = ruleWithPlaceholders;
    const numberOfPlaceholders = (rule.match(new RegExp('{', 'g')) ?? []).length;
    this.ruleNonColumnValues = [];

    for(let i = 0; i < numberOfPlaceholders; i++) {
      const placeholder = rule.substring(
        rule.indexOf('{'),
        rule.indexOf('}') + 1
      );
    
      const contextColumns = this.getMappingColumns();
      const validColumn = contextColumns.find(x => x.name === placeholder.replace('{', '').replace('}', ''));

      if(!validColumn) {
        if(placeholder.includes('{') && placeholder.includes('}') && 
        !this.ruleNonColumnValues.find(x => x === placeholder.replace('{', '').replace('}', '')))
          {this.ruleNonColumnValues.push(placeholder.replace('{', '').replace('}', ''));}
      }
      rule = rule.replace(placeholder, '');
    }
    this.form.controls.rule.markAsDirty();
    this.form.controls.rule.markAsTouched();
  }

  public nonColumnValuesExist(): boolean { 
    return this.ruleNonColumnValues.length > 0;
  }

  private filterMappingByCustomer(mappings: Mapping[]): void {
    if (!this.customerMappingIds) {
      this.mappings = [];
      return;
    }

    this.mappings = mappings
      .filter((mapping) => this.customerMappingIds.includes(mapping.id))
      .sort((a, b) => 0 - (a.name < b.name ? 1 : -1));
  }

  private replacePlaceholdersWithArgs(ruleWithPlaceholders: string): string {
    let ruleWithArgs = ruleWithPlaceholders;
    const contextColumns = this.getMappingColumns();
    for (const column of contextColumns) {
      const indexOfPlaceholder = ruleWithPlaceholders.indexOf(
        `{${column.name}}`
      );
      if (indexOfPlaceholder > -1) {
        const regEx = isNaN(+column.name)
          ? new RegExp(`{${column.name}}`, 'g')
          : `{${column.name}}`; // replace all
        ruleWithArgs = ruleWithArgs.replace(
          regEx,
          this.setArg(column.position)
        );
      }
    }

    return ruleWithArgs;
  }

  private replaceArgsWithPlaceholders(ruleWithArgs: string): string {
    let ruleWithPlaceholders = ruleWithArgs;
    const contextColumns = this.getMappingColumns();

    const possibleArgs = contextColumns.map((x) => ({
      arg: this.setArg(x.position),
      argValue: x.name,
    }));

    for (const arg of possibleArgs) {
      const indexOfArg = ruleWithPlaceholders.indexOf(`${arg.arg}`);
      if (indexOfArg > -1) {
        const regEx = new RegExp(`${arg.arg}`, 'g'); // replace all
        ruleWithPlaceholders = ruleWithPlaceholders.replace(
          regEx,
          this.setPlaceholder(arg.argValue)
        );
      }
    }

    return ruleWithPlaceholders;
  }
  
  private setArg(columnPosition: number) {
    return `arg${columnPosition}`;
  }

  private setPlaceholder(placeholderValue: string) {
    return `{${placeholderValue}}`;
  }
}
